import { useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Search, Shield, Wand2, Plus, X } from "lucide-react"
import clsx from "clsx"
import { api, ApiError, type AdminUser, type Tag } from "@/lib/types"
import { GlarchiveSubNav } from "@/components/GlarchiveSubNav"
import { TagChip } from "@lilbillbiscuit/account-ui"
import { formatRelative } from "@/lib/format"

export function AdminUsers() {
  const qc = useQueryClient()
  const [q, setQ] = useState("")
  const [activeUserId, setActiveUserId] = useState<string | null>(null)

  const users = useQuery<{ items: AdminUser[]; total: number }>({
    queryKey: ["adminUsers", q],
    queryFn: () => api("/api/admin/users", { query: { q: q || undefined, limit: 100 } }),
  })
  const tags = useQuery<{ tags: Tag[] }>({
    queryKey: ["adminTagsAll"],
    queryFn: () => api("/api/admin/tags"),
  })

  const setAdmin = useMutation<unknown, ApiError, { id: string; value: boolean }>({
    mutationFn: ({ id, value }) => api(`/api/admin/users/${id}/admin`, { method: "POST", json: { value } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adminUsers"] }),
  })
  const runAuto = useMutation<unknown, ApiError, string>({
    mutationFn: (id) => api(`/api/admin/users/${id}/auto-tag`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adminUsers"] }),
  })

  const activeUser = useMemo(
    () => users.data?.items.find((u) => u.id === activeUserId) ?? null,
    [users.data, activeUserId]
  )

  return (
    <div className="space-y-6">
      <GlarchiveSubNav />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-semibold">Users</h1>
        <div className="relative">
          <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-th-fg/40" />
          <input className="input w-64 pl-8" placeholder="Search email or name" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_22rem]">
        <div className="overflow-hidden rounded-2xl border border-th-glass/[0.08]">
          <table className="w-full text-sm">
            <thead className="bg-th-glass/[0.04] text-left text-xs uppercase tracking-wide text-th-fg/50">
              <tr>
                <th className="px-4 py-2">User</th>
                <th className="px-4 py-2">Tags</th>
                <th className="px-4 py-2 w-32">Last seen</th>
                <th className="px-4 py-2 w-20 text-center">Admin</th>
              </tr>
            </thead>
            <tbody>
              {users.data?.items.map((u) => (
                <tr
                  key={u.id}
                  tabIndex={0}
                  role="button"
                  aria-pressed={u.id === activeUserId}
                  onClick={() => setActiveUserId(u.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      setActiveUserId(u.id)
                    }
                  }}
                  className={clsx(
                    "cursor-pointer border-t border-th-glass/[0.06] transition-colors hover:bg-th-glass/[0.06] focus:bg-th-glass/[0.08] focus:outline-none",
                    u.id === activeUserId && "bg-th-glass/[0.08]"
                  )}
                >
                  <td className="px-4 py-2">
                    <div className="font-medium text-th-fg">{u.name || u.email}</div>
                    <div className="text-xs text-th-fg/50">{u.email}</div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {u.tag_slugs.length > 0
                        ? u.tag_slugs.slice(0, 5).map((s) => <TagChip key={s} slug={s} />)
                        : <span className="text-xs text-th-fg/40">no tags</span>}
                      {u.tag_slugs.length > 5 && <span className="tag-badge">+{u.tag_slugs.length - 5}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-th-fg/60">{formatRelative(u.last_seen_at_ms)}</td>
                  <td className="px-4 py-2 text-center">
                    <button
                      onClick={(e) => { e.stopPropagation(); setAdmin.mutate({ id: u.id, value: u.is_admin !== 1 }) }}
                      title={u.is_admin === 1 ? "Revoke admin" : "Grant admin"}
                      className={clsx("inline-flex rounded-md p-1 transition-colors", u.is_admin === 1 ? "text-th-accent" : "text-th-fg/30 hover:text-th-fg")}
                    >
                      <Shield size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {!users.isLoading && (users.data?.items.length ?? 0) === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-th-fg/40">No users match.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <aside className="glass-card sticky top-20 self-start">
          {activeUser ? (
            <UserDetail
              user={activeUser}
              allTags={tags.data?.tags ?? []}
              onRunAutoTag={() => runAuto.mutate(activeUser.id)}
            />
          ) : (
            <div className="py-12 text-center text-sm text-th-fg/40">
              Select a user to manage their tags.
            </div>
          )}
        </aside>
      </div>
    </div>
  )
}

function UserDetail({ user, allTags, onRunAutoTag }: { user: AdminUser; allTags: Tag[]; onRunAutoTag: () => void }) {
  const qc = useQueryClient()
  const granted = new Set(user.tag_slugs)
  const ungranted = allTags.filter((t) => !granted.has(t.slug))

  const diff = useMutation<unknown, ApiError, { add?: string[]; remove?: string[] }>({
    mutationFn: ({ add = [], remove = [] }) =>
      api(`/api/admin/users/${user.id}/tags`, { method: "POST", json: { add, remove } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adminUsers"] }),
  })

  return (
    <div className="space-y-4">
      <div>
        <div className="font-heading text-lg font-semibold">{user.name || user.email}</div>
        <div className="text-xs text-th-fg/50">{user.email}</div>
      </div>
      <div>
        <div className="mb-2 text-xs uppercase tracking-wide text-th-fg/50">Current tags</div>
        <div className="flex flex-wrap gap-1.5">
          {user.tag_slugs.length === 0 && <span className="text-sm text-th-fg/40">none</span>}
          {user.tag_slugs.map((s) => (
            <button
              key={s}
              disabled={diff.isPending}
              className="tag-badge-accent inline-flex items-center gap-1 cursor-pointer hover:bg-th-accent/25 disabled:opacity-50"
              onClick={() => diff.mutate({ remove: [s] })}
            >
              {s} <X size={10} />
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="mb-2 text-xs uppercase tracking-wide text-th-fg/50">Grant</div>
        <div className="flex flex-wrap gap-1.5">
          {ungranted.length === 0 && <span className="text-sm text-th-fg/40">user has every tag</span>}
          {ungranted.map((t) => (
            <button
              key={t.slug}
              disabled={diff.isPending}
              className="tag-badge inline-flex items-center gap-1 hover:border-th-accent/40 hover:text-th-accent disabled:opacity-50"
              onClick={() => diff.mutate({ add: [t.slug] })}
            >
              <Plus size={10} /> {t.slug}
            </button>
          ))}
        </div>
      </div>
      {diff.error && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-400">
          {diff.error.serverMessage ?? "Tag update failed"}
        </div>
      )}
      <button className="btn" onClick={onRunAutoTag}>
        <Wand2 size={13} /> Re-run yale.edu auto-tag
      </button>
    </div>
  )
}
